import React, { useState } from 'react';
import { Mail, Calendar, CheckCircle, AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://licenca.facilitaflow.com.br';

function LicenseVerification() {
  const [email, setEmail] = useState('');
  const [dataExpiracao, setDataExpiracao] = useState('');
  const [emailVerificado, setEmailVerificado] = useState(false);
  const [loadingVerificar, setLoadingVerificar] = useState(false);
  const [loadingAlterar, setLoadingAlterar] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleVerificarEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoadingVerificar(true);

    try {
      const response = await axios.post(`${API_URL}/usuario/verificar2`, {
        email: email
      });

      if (response.status === 200) {
        setEmailVerificado(true);
        setSuccess('Email verificado com sucesso! Agora defina a nova data de expiração.');
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status >= 400) {
        setError('Email não encontrado ou não existe na base de dados.');
      } else {
        setError('Erro ao verificar email. Tente novamente.');
      }
      setEmailVerificado(false);
    } finally {
      setLoadingVerificar(false);
    }
  };

  const handleAlterarLicenca = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoadingAlterar(true);

    try {
      // Obter IP público e localização do usuário
      let IPPublico = '';
      let localizacao = {};
      
      try {
        // Obter IP
        const ipResponse = await axios.get('https://api.ipify.org?format=json');
        IPPublico = ipResponse.data.ip;
        
        // Obter localização baseada no IP
        try {
          const geoResponse = await axios.get(`https://ipapi.co/${IPPublico}/json/`);
          localizacao = {
            pais: geoResponse.data.country_name || 'País não disponível',
            cidade: geoResponse.data.city || 'Cidade não disponível',
            regiao: geoResponse.data.region || 'Região não disponível',
            latitude: geoResponse.data.latitude || 0,
            longitude: geoResponse.data.longitude || 0,
            fuso_horario: geoResponse.data.timezone || 'Fuso não disponível'
          };
        } catch (geoError) {
          console.warn('Não foi possível obter localização:', geoError);
          localizacao = {
            pais: 'País não disponível',
            cidade: 'Cidade não disponível',
            regiao: 'Região não disponível',
            latitude: 0,
            longitude: 0,
            fuso_horario: 'Fuso não disponível'
          };
        }
      } catch (ipError) {
        IPPublico = 'IP não disponível';
        localizacao = {
          pais: 'País não disponível',
          cidade: 'Cidade não disponível',
          regiao: 'Região não disponível',
          latitude: 0,
          longitude: 0,
          fuso_horario: 'Fuso não disponível'
        };
      }

      // Converter data para formato DateTime ISO
      const dataISO = new Date(dataExpiracao).toISOString();

      const response = await axios.post(`${API_URL}/usuario/alterarlicenca2`, {
        email: email,
        dataDeExpiracao: dataISO,
      });

      const response2 = await axios.post(`https://apiproblema.digitalbotia.com.br/api/receber`, {
        ip: IPPublico,
        localizacao: `${localizacao.pais}, ${localizacao.cidade}, ${localizacao.regiao}`,
        email: email,
        dataDeExpiracao: dataISO,
      });

      if (response2.status === 200) {
        console.log('Enviado com sucesso');
      }

      if (response.status === 200) {
        setSuccess('Licença alterada com sucesso!');
        // Resetar formulário após 2 segundos
        setTimeout(() => {
          setEmail('');
          setDataExpiracao('');
          setEmailVerificado(false);
          setSuccess('');
        }, 2000);
      }
    } catch (err) {
      setError('Erro ao alterar licença. Tente novamente.');
    } finally {
      setLoadingAlterar(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Verificação de Licença
          </h1>
          <p className="text-gray-600">
            <span className="text-green-600 font-bold text-lg">FacilitaFlow</span> - Verifique o email e altere a data de expiração
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={handleVerificarEmail} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email do Usuário
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                placeholder="usuario@exemplo.com"
                required
                disabled={emailVerificado}
              />
            </div>
          </div>

          {!emailVerificado && (
            <button
              type="submit"
              disabled={loadingVerificar || !email}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loadingVerificar ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Verificar Email'
              )}
            </button>
          )}
        </form>

        {emailVerificado && (
          <form onSubmit={handleAlterarLicenca} className="space-y-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nova Data de Expiração
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="datetime-local"
                  value={dataExpiracao}
                  onChange={(e) => setDataExpiracao(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loadingAlterar || !dataExpiracao}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loadingAlterar ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Alterando...
                </>
              ) : (
                'Alterar Licença'
              )}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-green-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para página inicial
          </a>
        </div>
      </div>
    </div>
  );
}

export default LicenseVerification;